import { Component, OnDestroy, OnInit } from '@angular/core';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { MatDialog } from '@angular/material/dialog';
import { Retailer } from '@src/app/model/retailer';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { ScopeService } from '@src/app/services/scope/scope.service';
import { Asset, id } from '@ess/jg-rule-executor';
import { PortalInvariant } from '@src/app/model/portal-invariant';
import { RuleEditorModalComponent } from '@src/app/components/modals/rule-editor-modal/rule-editor-modal.component';
import { cloneDeep } from 'lodash';
import { first } from 'rxjs/operators';
import { AssetService } from '@src/app/services/asset/asset.service';
import { AssetDialogComponent } from '@src/app/components/asset-dialog/asset-dialog.component';
import { PriceStock } from '@src/app/model/price-stock';
import { ItemPriceStock, WoodPriceStock } from '@src/app/model/price-stock-data';
import {
  createRuleListFromInvariants,
  RuleData,
} from '@src/app/components/modals/rule-editor-modal/rule-editor-modal.utils';
import { JsonModal } from '@src/app/components/utils/json-modal';
import { TypedFormModal } from '@src/app/components/utils/typed-form-modal';
import { TypedFormModalDataObject } from '@src/app/model/typed-form-modal-data-object';
import { RetailerInput, SubjectType } from '@src/app/generated/globalTypes';
import { fixedEnvironment } from '@src/environments/environment.fixed';

@Component({
  selector: 'app-retailer-list',
  templateUrl: './retailer-settings.component.html',
  styleUrls: ['./retailer-settings.component.scss'],
  standalone: false,
})
export class RetailerSettingsComponent implements OnDestroy, OnInit {
  private _componentDestroyed$: Subject<void> = new Subject();

  public displayedColumns: string[] = ['id', 'name', 'email', 'location', 'currency', 'action', 'assets'];
  public currentRetailer: Retailer[];

  private _retailerFailMsg: string = 'retailers.upload.failure';
  private _stockFailMsg: string = 'retailers.uploadStock.failure';
  private _fetchFailMsg: string = 'retailers.ruleTemplate.failure';
  private _assetFailMsg: string = 'retailers.uploadAsset.fail';
  private _assetSuccessMsg: string = 'retailers.uploadAsset.success';
  private _busyMsg: string = 'retailers.uploadAsset.busy';
  private _skipFields: string[] = [
    'retailerOptions.font', 'retailerOptions.banner', 'retailerOptions.favicon', 'retailerOptions.safetyEnvironment.invariants',
  ];

  public inProgress: boolean = false;

  constructor(
    private _apolloService: ApolloService,
    private _dialog: MatDialog,
    private _translateService: TranslateService,
    private _assetService: AssetService,
    private _scopeService: ScopeService
  ) {
    this._apolloService.getCurrentRetailer$()
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe(retailer => this.currentRetailer = [retailer]);
  }

  public ngOnInit(): void {
    this._scopeService.currentScope$.asObservable()
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe(() => this._apolloService.getCurrentRetailerRefresh());
  }

  public openEditRetailerJsonDialog(retailer: Retailer): void {
    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      'retailer',
      retailer ? retailer : new Retailer(),
      'retailers.edit.header',
      'buttons.submit',
      this._skipFields
    ));
    JsonModal.useJsonDialog(
      dialogRef,
      (json => this._apolloService.updateRetailer$(retailer?.id, json)).bind(this._apolloService),
      this._translateService.instant(this._retailerFailMsg),
      this._apolloService.getCurrentRetailerRefresh.bind(this._apolloService)
    );
  }

  public openEditRetailerFormDialog(retailer: Retailer): void {
    const dialogRef = TypedFormModal.openTypedFormModalDialog(
      this._dialog, new TypedFormModalDataObject(
        'retailer',
        SubjectType.RetailerType,
        retailer,
        retailer.id,
        this._skipFields,
        'retailers.edit.header',
      )
    );
    TypedFormModal.useFormDialog(
      dialogRef,
      (
        (edited: RetailerInput) =>
          this._apolloService.updateRetailer$(retailer.id, Retailer.toJsonInput(edited))
      ).bind(this._apolloService),
      this._translateService.instant(this._retailerFailMsg),
      this._apolloService.getCurrentRetailerRefresh.bind(this._apolloService)
    );
  }

  public openUploadItemStockDialog(retailer?: Retailer): void {
    const singleStock = retailer?.stock?.filter(ps => ps.data instanceof ItemPriceStock);
    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      'priceStocks',
      singleStock && singleStock.length ? singleStock : [PriceStock.defaultSingle],
      'retailers.uploadItemStock.header',
      'buttons.submit',
      [],
      false,
      true
    ));
    JsonModal.useScopedJsonDialog(
      dialogRef,
      this._apolloService.uploadSinglePriceStocks$.bind(this._apolloService),
      this._translateService.instant(this._stockFailMsg),
      this._apolloService.getCurrentRetailerRefresh.bind(this._apolloService)
    );
  }

  public openUploadWoodStockDialog(retailer?: Retailer): void {
    const woodStock = retailer?.stock?.filter(ps => ps.data instanceof WoodPriceStock);
    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      'priceStocks',
      woodStock && woodStock.length ? woodStock : [PriceStock.defaultWood],
      'retailers.uploadWoodStock.header',
      'buttons.submit',
      [],
      false,
      true
    ));
    JsonModal.useScopedJsonDialog(
      dialogRef,
      this._apolloService.uploadWoodPriceStocks$.bind(this._apolloService),
      this._translateService.instant(this._stockFailMsg),
      this._apolloService.getCurrentRetailerRefresh.bind(this._apolloService)
    );
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
    this._componentDestroyed$.complete();
  }

  public openEditSafetyEnvironmentsDialog(
    retailer: Retailer
  ): void {
    // Set up the rule list
    const ruleList: RuleData[] = createRuleListFromInvariants(retailer.safetyEnvironment.invariants);
    const additionalFieldsTemplate = new PortalInvariant(
      id('templateId'), // Will be replaced
      '',
      new Map(),
      new Map(),
      '',
      ''
    );

    // Open the dialog
    const dialogRef = this._dialog.open(RuleEditorModalComponent, {
      data: {
        ruleTypeText: 'invariant',
        ruleList,
        crud: true,
        readOnly: false,
        additionalFieldsTemplate,
        baseTranslationKey: 'retailers.updateSafetyEnvironment',
      },
      minWidth: fixedEnvironment.jsonModalWidthLarge,
    });

    const updater = (updated) => {
      // Parse the edited rules
      const updatedRetailer = cloneDeep(retailer);
      updatedRetailer.safetyEnvironment.invariants = updated.map((x) => {
        const updatedInvariant = x.additionalFields as PortalInvariant;
        updatedInvariant.id = id(x.ruleId);
        updatedInvariant.rule = x.ruleCode;
        updatedInvariant.correction = x.correctionCode;
        return updatedInvariant;
      });

      const newRetailerInput: JSON = Retailer.toJsonInput(updatedRetailer.toGqlInput());

      return this._apolloService.updateRetailer$(updatedRetailer.id, newRetailerInput);
    };
    const onError = dialogRef.componentInstance.showError.bind(dialogRef.componentInstance);

    JsonModal.useRuleDialog(
      dialogRef,
      updater.bind(this._apolloService),
      this._translateService.instant(this._fetchFailMsg, { ruleType: 'invariant' }),
      this._apolloService.getCurrentRetailerRefresh.bind(this._apolloService),
      onError
    );
  }

  public uploadAsset(
    event: any,
    type: string,
    retailer: Retailer,
    input: HTMLInputElement,
    helpItemOrder?: number,
    fontType?: string,
    iconType?: string
  ): void {
    if (!this.inProgress) {
      this.inProgress = true;
      const scope = this._scopeService.currentScope$.getValue()?.id;
      if (event.target.files.length > 0 && scope) {
        const assetMap = new Map().set('retailer', retailer.id).set('assetType', type);
        if (helpItemOrder) {
          assetMap.set('helpItemOrder', helpItemOrder);
        }
        if (fontType) {
          assetMap.set('fontType', fontType);
        }
        if (iconType) {
          assetMap.set('iconType', iconType);
        }
        this._assetService.uploadAsset$(event.target.files[0], assetMap)
          .pipe(first())
          .subscribe({
            next: (url) => {
              this.inProgress = false;
              input.value = '';
              if (!url) {
                window.alert(this._translateService.instant(this._assetFailMsg));
              } else {
                this._apolloService.getCurrentRetailerRefresh();
                window.alert(this._translateService.instant(this._assetSuccessMsg));
              }
            },
            error: (_) => {
              input.value = '';
              this.inProgress = false;
              window.alert(this._translateService.instant(this._assetFailMsg));
            },
          });
      }
    } else {
      window.alert(this._translateService.instant(this._busyMsg));
    }
  }

  public openAsset(asset: Asset) {
    this._dialog.open(AssetDialogComponent, { data: { asset } });
  }
}
